import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './hooks';
import { fetchBillingThunk, fetchCostCatalogThunk } from '@/store/billingSlice';
import type { BillingTier, SubscriptionStatus, TierLimits, TierUsage } from '@/types/billing';

export interface TierInfo {
  /** WEB-1290: 'UNKNOWN' when the server hides the real tier — never coerced to 'FREE'. */
  tier: BillingTier;
  status: SubscriptionStatus;
  limits: TierLimits | null;
  usage: TierUsage | null;
  isLoading: boolean;
  canCreateProject: boolean;
  canCreateQuiz: boolean;
  canCreatePaywall: boolean;
  /** WEB-647: A/B-тесты доступны на тарифе (FREE → false). */
  canUseSmartLinks: boolean;
  /** WEB-645: публикация доступна на тарифе (FREE → false). */
  canPublish: boolean;
  isPastDue: boolean;
}

export function useTier(): TierInfo {
  const dispatch = useAppDispatch();
  const hasFetched = useRef(false);

  // Defensive — tests may construct stores without the billing reducer
  // registered. In production state.billing is always present. WEB-1290:
  // the fallback is 'UNKNOWN', not 'FREE' — an absent slice is not evidence
  // of a free tier, and 'UNKNOWN' is what every consumer already treats as
  // "don't block, don't disclose" (see types/billing.ts BillingTier doc).
  const tier = useAppSelector((state) => state.billing?.tier ?? 'UNKNOWN');
  const subscription = useAppSelector(
    (state) => state.billing?.subscription ?? null,
  );
  const limits = useAppSelector((state) => state.billing?.limits ?? null);
  const usage = useAppSelector((state) => state.billing?.usage ?? null);
  const isLoading = useAppSelector(
    (state) => state.billing?.isLoadingBilling ?? false,
  );

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      dispatch(fetchBillingThunk());
    }
  }, [dispatch]);

  // WEB-184: the AI cost catalog lives in Redux so «≈Ncr» price badges
  // resolve everywhere after one load. WEB-3911: the store blanks it on an
  // org/project switch (store/index.ts resetCentralSlices), so it is re-read
  // whenever it is empty, not loading and not yet tried in this context —
  // one place. Checked against the LIVE store inside a thunk: several useTier
  // instances in one commit must not send several requests; a server refusal
  // leaves `costCatalogAttempted` set, so there is no retry loop.
  const catalogMissing = useAppSelector(
    (state) =>
      state.billing != null &&
      state.billing.costCatalog == null &&
      !state.billing.isLoadingCostCatalog &&
      !state.billing.costCatalogAttempted,
  );
  useEffect(() => {
    if (!catalogMissing) return;
    dispatch((d, getState) => {
      const b = getState().billing;
      if (b && b.costCatalog == null && !b.isLoadingCostCatalog && !b.costCatalogAttempted) {
        void d(fetchCostCatalogThunk());
      }
    });
  }, [dispatch, catalogMissing]);

  const status: SubscriptionStatus = subscription?.status ?? 'ACTIVE';

  const canCreateProject = limits == null || usage == null
    ? true
    : usage.projects < limits.maxProjects;

  // WEB-3840 (owner decision 25.10, item 54; ADR-0024 p. 1): the FREE
  // combined funnel-unit quota (WEB-1080, "1 quiz OR 1 paywall") is gone —
  // the backend gate counts quizzes and paywalls per type on every tier, so
  // the cabinet does the same (unpaid gets the "unlimited" ceiling 9 999).
  const canCreateQuiz = limits == null || usage == null
    ? true
    : usage.quizzes < limits.maxQuizzes;

  const canCreatePaywall = limits == null || usage == null
    ? true
    : usage.paywalls < limits.maxPaywalls;

  const isPastDue = status === 'PAST_DUE' || status === 'UNPAID';

  // WEB-647: булев feature-гейт (аналог publishAllowed). Блокируем ТОЛЬКО при
  // явном false; отсутствие флага/limits → доступ (safe default, не false-block).
  const canUseSmartLinks = limits?.smartLinksAllowed !== false;

  // WEB-645: тот же safe-default паттерн для публикации (FREE publishAllowed=false).
  const canPublish = limits?.publishAllowed !== false;

  return {
    tier,
    status,
    limits,
    usage,
    isLoading,
    canCreateProject,
    canCreateQuiz,
    canCreatePaywall,
    canUseSmartLinks,
    canPublish,
    isPastDue,
  };
}
